import type { StreamPoint } from './workoutStream';
import { trainingZoneNumber } from './trainingZones';
// Match the timeline gap policy: preserve smart recording, exclude long pauses.
const STREAM_CHART_PATH_GAP_SEC = 30;

export interface AthleteZoneTime {
  label: string;
  /** Configured identity survives sorting and zero-time rows. */
  zoneNumber?: number;
  minimum: number;
  maximum: number | null;
  time_in_seconds: number;
  percent_in_zone: number;
  unclassified?: boolean;
  invalidConfiguration?: boolean;
}

/** Reclassify the recorded samples using the athlete's current absolute zone boundaries.
 * Weight smart-recording samples by elapsed time; do not fill long recording gaps or pauses.
 * Shared endpoints belong to the higher zone. Missing sensor values are not zero readings.
 */
export function athleteZoneDistribution(
  stream: StreamPoint[], settings: Record<string, unknown> | null,
  metric: 'power' | 'heartRate',
): AthleteZoneTime[] | null {
  const key = metric === 'power' ? 'power_zones' : 'heart_rate_zones';
  const block = settings?.[key] as { zones?: unknown[] } | undefined;
  if (!Array.isArray(block?.zones) || !block.zones.length) return null;
  const zones: AthleteZoneTime[] = [];
  for (const [index, row] of block.zones.entries()) {
    if (!row || typeof row !== 'object') return null;
    const z = row as { label?: string; minimum?: unknown; maximum?: unknown };
    if (z.minimum == null || z.minimum === '' || z.maximum === '') return null;
    const minimum = Number(z.minimum);
    const maximum = z.maximum == null ? null : Number(z.maximum);
    if (!Number.isFinite(minimum) || minimum < 0 || (maximum != null && (!Number.isFinite(maximum) || maximum < minimum))) return null;
    zones.push({ label: z.label || `Z${index + 1}`, zoneNumber: trainingZoneNumber(z.label ?? '') ?? index + 1, minimum, maximum, time_in_seconds: 0, percent_in_zone: 0 });
  }
  zones.sort((a, b) => a.minimum - b.minimum);
  if (zones.some((z, i) => i > 0 && (zones[i - 1].maximum == null || zones[i - 1].maximum! > z.minimum))) {
    return zones.map(z => ({ ...z, invalidConfiguration: true }));
  }
  if (!stream.length) return [];
  const intervals = stream.slice(1).map((p, i) => p.timeSeconds - stream[i].timeSeconds)
    .filter(d => d > 0 && d <= STREAM_CHART_PATH_GAP_SEC).sort((a, b) => a - b);
  const nominal = intervals.length ? intervals[Math.floor(intervals.length / 2)] : 1;
  let total = 0;
  let outside = 0;
  stream.forEach((p, i) => {
    const value = p[metric];
    if (p.paused || value == null || !Number.isFinite(value) || value < 0 || (metric === 'heartRate' && value === 0)) return;
    const delta = i + 1 < stream.length ? stream[i + 1].timeSeconds - p.timeSeconds : nominal;
    if (!Number.isFinite(delta) || delta <= 0) return;
    const seconds = delta > STREAM_CHART_PATH_GAP_SEC ? nominal : delta;
    total += seconds;
    // Iterate in reverse so a shared boundary is counted exactly once in the higher zone.
    const zone = [...zones].reverse().find(z => value >= z.minimum && (z.maximum == null || value <= z.maximum));
    if (zone) zone.time_in_seconds += seconds;
    else outside += seconds;
  });
  if (!total) return [];
  if (outside) zones.push({ label: '', minimum: 0, maximum: null, time_in_seconds: outside, percent_in_zone: 0, unclassified: true });
  return zones.map(z => ({ ...z, percent_in_zone: z.time_in_seconds / total * 100 }));
}
